<template>
  <section v-if="outcome" class="outcome-editor">
    <div class="outcome-top">
      <div>
        <h3>Lead outcome</h3>
        <p>What happened after this inquiry?</p>
      </div>
      <span class="outcome-state" :class="outcome.state">{{
        labels[outcome.state]
      }}</span>
    </div>
    <p v-if="outcome.legacy_review_required" class="outcome-warning">
      Previously marked Converted. Please confirm whether the business was
      hired.
    </p>
    <p v-if="outcome.proposed_correction" class="outcome-warning">
      A correction is awaiting manager review. The verified CRM outcome is still
      in place.
    </p>
    <form @submit.prevent="save">
      <label
        >Outcome<select v-model="draft.state">
          <option v-for="(label, key) in labels" :key="key" :value="key">
            {{ label }}
          </option>
        </select></label
      >
      <p class="outcome-hint">
        Booked is an appointment. Hired us confirms a job, without implying
        completion or payment.
      </p>
      <label v-if="draft.state === 'booked'"
        >Appointment (your local time)<input
          v-model="draft.appointment"
          type="datetime-local"
      /></label>
      <label
        >Reason or note (optional)<textarea
          v-model="draft.reason"
          rows="2"
          maxlength="2000"
        ></textarea>
      </label>
      <div class="outcome-grid">
        <label
          >Owner<select v-model="draft.owner_id">
            <option :value="null">Unassigned</option>
            <option :value="tenantId">Account owner</option>
          </select></label
        >
        <label v-if="!resolved"
          >Next review (your local time)<input
            v-model="draft.next_review"
            type="datetime-local"
        /></label>
      </div>
      <label v-if="!resolved"
        >Snooze until (optional, your local time)<input
          v-model="draft.snooze"
          type="datetime-local"
      /></label>
      <div class="outcome-actions">
        <button :disabled="busy" type="submit">
          {{ busy ? 'Saving…' : 'Save outcome' }}</button
        ><button
          type="button"
          class="outcome-secondary"
          :disabled="busy"
          @click="createLink"
        >
          Create update link
        </button>
      </div>
    </form>
    <label v-if="link"
      >Single-use update link · expires in 7 days<input
        :value="link"
        readonly
        aria-label="Update link"
        @focus="$event.target.select()"
    /></label>
    <p v-if="link" class="outcome-hint">
      For authorized business staff. Creating another link revokes this one. No
      message has been sent.
    </p>
    <p v-if="notice" role="status">{{ notice }}</p>
    <p v-if="error" class="outcome-error" role="alert">{{ error }}</p>
    <details>
      <summary>Jobs and revenue ({{ outcome.jobs.length }})</summary>
      <outcome-jobs :outcome="outcome" :lead-id="leadId" @saved="accept" />
    </details>
    <details v-if="outcome.authority === 'crm' || outcome.override_reason">
      <summary>CRM authority and manager review</summary>
      <p>
        Source: {{ outcome.authority }} · Last reconciled:
        {{ outcome.reconciled_at || 'Not available' }} UTC
      </p>
      <p v-if="outcome.override_reason">
        Override: {{ outcome.override_reason }}
      </p>
      <button
        class="outcome-secondary"
        :disabled="busy"
        @click="override('set')"
      >
        Apply selected outcome as an override
      </button>
      <button
        v-if="outcome.override_reason"
        class="outcome-secondary"
        :disabled="busy"
        @click="override('clear')"
      >
        Clear override and reconcile
      </button>
      <p class="outcome-hint">Manager access and a reason are required.</p>
    </details>
    <details>
      <summary>Change history and duplicate review</summary>
      <button type="button" class="outcome-secondary" @click="loadHistory">
        Load history
      </button>
      <ul>
        <li v-for="item in history" :key="item.id">
          {{ item.received_at }} UTC · {{ item.source }} ·
          {{ item.reason || 'Outcome or job update' }} · Actor
          {{ item.actor_id || 'Scoped update link' }}
        </li>
      </ul>
      <label
        >Duplicate lead ID<input v-model="aliasId" type="number" min="1"
      /></label>
      <p class="outcome-hint">
        Only explicitly reviewed duplicates without prior reviewed outcome/job
        data can be linked. Distinct opportunities must stay separate. Add a
        reason above.
      </p>
      <button
        class="outcome-secondary"
        :disabled="busy || !aliasId"
        @click="alias('link', Number(aliasId))"
      >
        Confirm duplicate
      </button>
      <p v-for="id in outcome.aliases" :key="id">
        Duplicate #{{ id }}
        <button
          class="outcome-secondary"
          :disabled="busy"
          @click="alias('unlink', id)"
        >
          Undo merge
        </button>
      </p>
    </details>
    <p class="outcome-hint">
      {{
        outcome.timezone
          ? 'Review timezone: ' + outcome.timezone
          : 'Timezone unverified; internal review dates use UTC.'
      }}
      · Reminders are disabled for review.
    </p>
    <router-link v-if="showQueue" to="/lead-outcomes"
      >Open outcome queue and conversion report →</router-link
    >
  </section>
</template>
<script>
import axios from 'axios'
import OutcomeJobs from './OutcomeJobs.vue'
const labels = {
  unknown: "Don't know",
  pending: 'Still working it',
  booked: 'Booked',
  won: 'Hired us',
  lost: 'Did not hire us',
}
export default {
  name: 'LeadOutcomeEditor',
  components: { OutcomeJobs },
  props: {
    leadId: { type: [Number, String], required: true },
    showQueue: { type: Boolean, default: true },
  },
  data() {
    return {
      labels,
      outcome: null,
      draft: {},
      tenantId: null,
      busy: false,
      error: '',
      notice: '',
      link: '',
      history: [],
      aliasId: '',
    }
  },
  computed: {
    resolved() {
      return ['won', 'lost'].includes(this.draft.state)
    },
  },
  watch: {
    leadId() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    config() {
      return {
        headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt },
      }
    },
    url(suffix = '') {
      return `${process.env.VUE_APP_API_ENDPOINT}/call-tracking/outcomes/${this.leadId}${suffix}`
    },
    uuid() {
      return window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = window.crypto.getRandomValues(new Uint8Array(1))[0] % 16
            return (c === 'x' ? r : (r & 3) | 8).toString(16)
          })
    },
    local(utc) {
      if (!utc) return ''
      const d = new Date(utc.replace(' ', 'T') + 'Z')
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
    },
    utc(local) {
      return local
        ? new Date(local).toISOString().slice(0, 19).replace('T', ' ')
        : null
    },
    accept(value, notify = true) {
      this.outcome = value
      this.tenantId = value.tenant_id
      this.draft = {
        state: value.state,
        reason: value.reason || '',
        owner_id: value.owner_id ? Number(value.owner_id) : null,
        appointment: this.local(value.appointment_at),
        next_review: this.local(value.next_review_at),
        snooze: this.local(value.snoozed_until),
      }
      if (notify) this.$emit('saved', value)
    },
    async load() {
      this.error = ''
      this.link = ''
      try {
        const { data } = await axios.get(this.url(), this.config())
        this.accept(data, false)
        this.$emit('available', true)
      } catch (e) {
        this.outcome = null
        this.$emit('available', false)
        if (!e.response || e.response.status !== 404)
          this.$emit(
            'load-error',
            'Could not load the lead outcome. Please reload.'
          )
      }
    },
    async update(fields) {
      this.busy = true
      this.error = ''
      this.notice = ''
      try {
        const { data } = await axios.post(
          this.url(),
          { version: this.outcome.version, request_id: this.uuid(), ...fields },
          this.config()
        )
        this.accept(data)
        this.notice = data.proposed_correction
          ? 'Saved as a proposed correction for manager review.'
          : 'Saved.'
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          'Could not save. Reload before trying again.'
      } finally {
        this.busy = false
      }
    },
    save() {
      const data = {
        state: this.draft.state,
        reason: this.draft.reason || null,
        owner_id: this.draft.owner_id,
      }
      if (this.draft.state === 'booked')
        data.appointment_at = this.utc(this.draft.appointment)
      if (this.draft.next_review !== this.local(this.outcome.next_review_at))
        data.next_review_at = this.utc(this.draft.next_review)
      if (this.draft.snooze !== this.local(this.outcome.snoozed_until))
        data.snoozed_until = this.utc(this.draft.snooze)
      return this.update(data)
    },
    override(action) {
      return this.update({
        override_action: action,
        state: this.draft.state,
        reason: this.draft.reason,
      })
    },
    alias(action, id) {
      return this.update({
        alias: { action, lead_id: id },
        reason: this.draft.reason,
      })
    },
    async createLink() {
      this.busy = true
      this.error = ''
      try {
        const { data } = await axios.post(this.url('/link'), {}, this.config())
        this.link = data.url
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          'Could not create update link.'
      } finally {
        this.busy = false
      }
    },
    async loadHistory() {
      try {
        const { data } = await axios.get(this.url('/history'), this.config())
        this.history = data.data
      } catch (e) {
        this.error = 'Could not load history.'
      }
    },
  },
}
</script>
<style>
.outcome-editor {
  border: 1px solid #dce5e7;
  border-radius: 12px;
  padding: 18px;
  background: #fff;
  color: #183039;
  font-size: 14px;
}
.outcome-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}
.outcome-top h3 {
  font-size: 17px;
  margin: 0;
}
.outcome-editor p {
  margin: 8px 0;
}
.outcome-state {
  border-radius: 20px;
  padding: 5px 12px;
  background: #edf1f3;
  white-space: nowrap;
  font-weight: 650;
}
.outcome-state.won {
  background: #def2e7;
  color: #12623b;
}
.outcome-state.lost {
  background: #fcebe7;
  color: #973626;
}
.outcome-state.booked,
.outcome-state.pending {
  background: #fff2cf;
  color: #76530b;
}
.outcome-editor label {
  display: block;
  font-weight: 600;
  margin-top: 12px;
}
.outcome-editor input,
.outcome-editor select,
.outcome-editor textarea {
  display: block;
  border: 1px solid #b9cbcf;
  border-radius: 7px;
  padding: 10px;
  width: 100%;
  background: #fff;
  color: #183039;
  font: inherit;
  margin-top: 5px;
}
.outcome-editor button {
  background: #276472;
  color: #fff;
  border-radius: 7px;
  padding: 10px 14px;
  font: inherit;
  cursor: pointer;
  min-height: 42px;
  border: 1px solid #276472;
}
.outcome-editor button:disabled {
  opacity: 0.55;
  cursor: wait;
}
.outcome-editor button.outcome-secondary {
  background: #fff;
  color: #276472;
}
.outcome-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.outcome-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.outcome-editor .outcome-hint {
  font-size: 12px;
  color: #60747c;
}
.outcome-warning {
  background: #fff6da;
  padding: 10px;
  border-radius: 7px;
}
.outcome-error {
  color: #ab2727;
}
.outcome-editor details {
  border-top: 1px solid #e3ebed;
  margin-top: 16px;
  padding-top: 12px;
}
.outcome-editor summary {
  font-weight: 650;
  cursor: pointer;
}
.outcome-editor button:focus-visible,
.outcome-editor input:focus,
.outcome-editor select:focus,
.outcome-editor textarea:focus {
  outline: 2px solid #68aaa0;
  outline-offset: 2px;
}
@media (max-width: 600px) {
  .outcome-grid {
    grid-template-columns: 1fr;
  }
  .outcome-top {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
